// What the notification feed's rows ARE, folded for a reader.
//
// The server keeps one row per fault per node (internal/episode): "open" — began and not announced
// over; "ended" — its recovery was announced; "event" — something that happened and has no end.
// A node going down is the same fault reported by every node that survives it, module by module, so
// the rows are folded here by SUBJECT (the fault without the node): one line, "on node10, node11".
//
// Measured 2026-10-10: two outages of one node put 200 rows in the list in half an hour, every error
// looked open for ever, and 16 of the 18 distinct breaches shown were already over on the nodes.

export interface FeedRow {
  id: number;
  module?: string;
  kind?: string;
  severity?: string;
  // A notification is a short title (summary), one line of impact (consequence), one line of what to do
  // (next) and the long explanation (detail), shown on request — notifications.go's row.
  summary?: string;
  consequence?: string;
  next?: string;
  detail?: string;
  link?: string;
  node?: string;
  created_at?: string;
  read_at?: string | null;
  // Absent from a server that predates episodes: such a row is an "event".
  state?: "open" | "ended" | "event";
  subject?: string;
  repeats?: number;
  last_at?: string | null;
  resolved_at?: string | null;
  resolved_note?: string;
  // The row CLOSES something (server: realtime.IsRecovery) — stored at info and already read.
  recovery?: boolean;
}

export interface FeedGroup {
  key: string;
  state: "open" | "ended" | "event";
  severity: string;
  module: string;
  kind: string;
  // The newest row's words.
  summary: string;
  consequence?: string;
  next?: string;
  detail?: string;
  nodes: string[];
  // Every row folded into this line (for "mark read").
  ids: number[];
  unread: boolean;
  // How many times it was said in all: rows folded plus each row's own repeats.
  times: number;
  startedAt?: string;
  lastAt?: string;
  endedAt?: string;
  endedNote?: string;
  // A recovery line: shown as "recovered"/"cleared" in the ok tone, never as an alarm, never unread.
  recovery: boolean;
}

const rank: Record<string, number> = { info: 0, warn: 1, error: 2, crit: 3 };

// Rows arrive newest first. Groups keep that order by their newest row; open groups are NOT moved to
// the top here — the caller splits by state, so "what is wrong now" is its own list.
export function groupFeed(rows: FeedRow[]): FeedGroup[] {
  const groups = new Map<string, FeedGroup>();
  const order: string[] = [];
  for (const r of rows) {
    const state = r.state ?? "event";
    // An event has no subject: the same thing said by several nodes folds by its words, with the node
    // names taken out (they are the only part that differs).
    const words = (r.summary ?? "").replace(/\bnode\d+\b/g, "node");
    const key =
      state === "event"
        ? `${r.recovery ? "recovery" : "event"}|${r.module}|${r.kind}|${words}`
        : `${state}|${r.subject ?? r.id}`;
    let g = groups.get(key);
    if (!g) {
      g = {
        key,
        state,
        severity: r.severity ?? "info",
        module: r.module ?? "",
        kind: r.kind ?? "",
        summary: r.summary ?? "",
        consequence: r.consequence,
        next: r.next || undefined,
        detail: r.detail || undefined,
        nodes: [],
        ids: [],
        unread: false,
        times: 0,
        startedAt: r.created_at,
        lastAt: r.last_at ?? r.created_at,
        endedAt: r.resolved_at ?? undefined,
        endedNote: r.resolved_note || undefined,
        recovery: !!r.recovery,
      };
      groups.set(key, g);
      order.push(key);
    }
    g.ids.push(r.id);
    g.times += 1 + (r.repeats ?? 0);
    if (r.node && !g.nodes.includes(r.node)) g.nodes.push(r.node);
    if (!r.read_at && !r.recovery) g.unread = true;
    if ((rank[r.severity ?? "info"] ?? 0) > (rank[g.severity] ?? 0)) g.severity = r.severity ?? g.severity;
    // The earliest start and the latest end of what was folded.
    if (r.created_at && (!g.startedAt || r.created_at < g.startedAt)) g.startedAt = r.created_at;
    if (r.resolved_at && (!g.endedAt || r.resolved_at > g.endedAt)) {
      g.endedAt = r.resolved_at;
      g.endedNote = r.resolved_note || g.endedNote;
    }
  }
  for (const g of groups.values()) g.nodes.sort();
  return order.map((k) => groups.get(k) as FeedGroup);
}

// "1m 20s", "2h 5m" — how long a fault lasted.
export function lasted(from?: string, to?: string): string {
  if (!from || !to) return "";
  const s = Math.max(0, Math.round((Date.parse(to) - Date.parse(from)) / 1000));
  if (!Number.isFinite(s)) return "";
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
  return `${Math.floor(s / 86400)}d ${Math.floor((s % 86400) / 3600)}h`;
}

// How one line is labelled, in every console that lists /me/notifications. A RECOVERY closes something
// an earlier row opened; shown with the alarm's own "info" pill it read as one more thing happening, so
// it gets its own word and the ok tone. Two words, because they are two claims: "cleared" (a breach or
// a blind spot closed — the summary qualifies one closed without a measurement: an alert silenced, an
// instance gone) and "recovered" (health came back, a task or peer succeeded again).
export function noticeBadge(g: {
  state?: string;
  severity?: string;
  kind?: string;
  recovery?: boolean;
}): { tone: "ok" | "warn" | "bad" | "neutral"; label: string } {
  if (g.recovery) return { tone: "ok", label: g.kind?.endsWith("_cleared") ? "cleared" : "recovered" };
  if (g.state === "ended") return { tone: "neutral", label: "ended" };
  const s = g.severity ?? "info";
  return { tone: s === "crit" || s === "error" ? "bad" : s === "warn" ? "warn" : "neutral", label: s };
}
